import ReactECharts from 'echarts-for-react';
import type { StyleConfig } from '../../../../types/bi';
import { useEChartsTheme } from '../../hooks/useEChartsTheme';
import { buildChartClickHandler } from '../../utils/drilldown';
import type { DrilldownEvent } from '../../utils/drilldown';

interface FunnelChartProps {
  data: { columns: string[]; rows: Record<string, unknown>[] };
  dimensions: string[];
  measures: { alias: string }[];
  style?: StyleConfig;
  onDrillDown?: (event: DrilldownEvent) => void;
}

export default function FunnelChart({
  data,
  dimensions,
  measures,
  style,
  onDrillDown,
}: FunnelChartProps) {
  const theme = useEChartsTheme();

  const dimKey = dimensions[0] || data.columns[0];
  const measureKey = measures[0]?.alias || data.columns[1];

  // Sort descending by value for natural funnel shape
  const funnelData = data.rows
    .map((r) => ({
      name: String(r[dimKey] ?? ''),
      value: Number(r[measureKey]) || 0,
    }))
    .sort((a, b) => b.value - a.value);

  // Calculate drop-off percentages
  const topValue = funnelData[0]?.value || 1;

  const option = {
    tooltip: {
      trigger: 'item' as const,
      formatter: (params: { name: string; value: number }) => {
        const pct = ((params.value / topValue) * 100).toFixed(1);
        return `${params.name}<br/>Value: ${params.value}<br/>Of total: ${pct}%`;
      },
    },
    legend: {
      orient: 'vertical' as const,
      right: 10,
      top: 20,
      ...(style?.legend_position === 'none' ? { show: false } : {}),
    },
    series: [
      {
        type: 'funnel' as const,
        left: '10%',
        top: 10,
        bottom: 10,
        width: '70%',
        sort: 'descending' as const,
        gap: 2,
        label: {
          show: true,
          position: 'inside' as const,
          formatter: (params: { name: string; value: number }) => {
            const pct = ((params.value / topValue) * 100).toFixed(0);
            return `${params.name}\n${pct}%`;
          },
        },
        data: funnelData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
      },
    ],
  };

  const events = buildChartClickHandler(dimensions, onDrillDown);

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      onEvents={events}
    />
  );
}
